'use client'

import { useVisibleProductCards } from '@/feature/product/hooks/useVisibleProductCards'
import { filterAvailableProducts } from '@/feature/product/services/product.filtering'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Drawer, DrawerAction, DrawerBody } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Loader } from '@/ui/loaders/loader/Loader'
import { useToast } from '@/ui/toast/Toast'
import { type ProductRowLinkedProducts_ProductFragment } from '@gql-generated/graphql'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TrackedLinkedProductRow } from './TrackedLinkedProductRow.client'

export const ProductRowLinkedProductsDrawer = memo(function ProductRowLinkedProductsDrawer({
  product,
  isDrawerOpen,
  addProductToCartAction,
  handleCloseDrawer,
}: {
  product?: ProductRowLinkedProducts_ProductFragment | null
  isDrawerOpen: boolean
  handleCloseDrawer: () => void
  addProductToCartAction: (product: {
    sku: string
    quantity: number
    price: number
  }) => Promise<void>
}) {
  const { t } = useTranslation(['productRowLinkedProducts'])
  const productCardsRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([])
  const [scrollableRoot, setScrollableRoot] = useState<HTMLElement | null>(null)
  const hasFoundScrollableRoot = useRef(false)
  const toast = useToast()

  const containerCallbackRef = useCallback((element: HTMLDivElement | null) => {
    if (!element || hasFoundScrollableRoot.current) return

    const parent = element.parentElement
    setScrollableRoot(parent)
    hasFoundScrollableRoot.current = true
  }, [])

  const handleAddProductToCart = useCallback(
    async ({ sku, quantity, price }: { sku: string; quantity: number; price: number }) => {
      await addProductToCartAction({ sku, quantity, price })

      toast.success({
        dedupe: false,
        message: t('productRowLinkedProducts:message.success'),
      })
    },
    [addProductToCartAction, toast, t],
  )

  const availableLinkedProducts = useMemo(
    () => filterAvailableProducts(product?.linkedProducts ?? []),
    [product?.linkedProducts],
  )

  const handleAddProductToCartFailed = useCallback(() => {
    toast.error({
      dedupe: false,
      message: t('productRowLinkedProducts:message.error'),
    })
  }, [toast, t])

  const { setProductCardsRefs, productCardsRefs: productCardsRefsHook } = useVisibleProductCards({
    itemListName: 'produits complémentaires - page panier',
    scrollableRoot,
  })

  useEffect(() => {
    productCardsRefs.current = availableLinkedProducts.map(
      (_, index) => productCardsRefs.current[index] || { current: null },
    )
  }, [availableLinkedProducts])

  useEffect(() => {
    if (
      productCardsRefs.current.length > 0 &&
      productCardsRefsHook.length !== productCardsRefs.current.length
    ) {
      setProductCardsRefs(productCardsRefs.current)
    }
  }, [availableLinkedProducts, setProductCardsRefs, productCardsRefsHook.length])

  useEffect(() => {
    if (!isDrawerOpen) {
      hasFoundScrollableRoot.current = false
      setScrollableRoot(null)
    }
  }, [isDrawerOpen])

  if (!product?.canAddToCart) {
    return null
  }

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onOpenChange={(isOpen) => !isOpen && handleCloseDrawer()}
      title={t('productRowLinkedProducts:drawer.title')}
      size="xl"
      aria-label={t('productRowLinkedProducts:drawer.title')}
      isDismissable={false}
    >
      <DrawerBody>
        {availableLinkedProducts.length === 0 ? (
          <Loader className="justify-self-center" />
        ) : (
          <div
            ref={containerCallbackRef}
            className="flex flex-col gap-4 [&_[data-testid='cart-item/details']]:desktop-md:w-auto [&_[data-testid='product-card/details']]:tablet:max-w-none [&_[data-testid='product-card/details']]:desktop-md:min-w-0"
          >
            {availableLinkedProducts.map((product, index) => {
              const ref = productCardsRefs.current[index] || { current: null }

              return (
                <TrackedLinkedProductRow
                  key={product.reference}
                  product={product}
                  index={index}
                  productRef={ref}
                  onAddProductToCart={handleAddProductToCart}
                  onAddProductToCartFailed={handleAddProductToCartFailed}
                />
              )
            })}
          </div>
        )}
      </DrawerBody>

      <DrawerAction
        type="custom"
        handle="to-cart"
        variant="primary"
        icon={<Icon.ShoppingBasket />}
        action={handleCloseDrawer}
      >
        {t('productRowLinkedProducts:drawer.button')}
      </DrawerAction>
    </Drawer>
  )
})
